import messages from "messages";
import { useCallback, useMemo } from "react";
import { useDrag } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  SidesheetType,
  selectCurrentSidesheet,
  selectFocusedBlockedInterval,
  showBlockIntervalDetails,
} from "store/calendarSidebarSlice";
import getClasses from "utils/getClasses";
import { useCalendarSidebarContext } from "../../../../../calendar-sidebar/context/CalendarSidebarContext";
import {
  CalendarDndDropResult,
  CalendarDndItem,
  CalendarDndItemTypes,
} from "../../../../constants/calendarDndTypes";
import { CalendarColumnPosition } from "../../utils/splitEventsIntoColumns";
import TimeslotButton from "../timeslot-button/TimeslotButton";
import styles from "./BlockedIntervalTimeslot.module.scss";

const BlockedIntervalTimeslot = ({
  blockedInterval,
  date,
  daySpan = 1,
  calendarColumnPosition,
}: {
  blockedInterval: CalendarBlockedInterval;
  date?: Date;
  daySpan?: number;
  calendarColumnPosition?: CalendarColumnPosition;
}) => {
  const dispatch = useDispatch();
  const currentSidesheet = useSelector(selectCurrentSidesheet);
  const focusedBlockedInterval = useSelector(selectFocusedBlockedInterval);
  const { requestCancelAddNewSession } = useCalendarSidebarContext();

  const [{ isAnotherAppointmentBeingDragged }] = useDrag<
    CalendarDndItem,
    CalendarDndDropResult,
    { isAnotherAppointmentBeingDragged: boolean }
  >(
    () => ({
      type: CalendarDndItemTypes.APPOINTMENT,
      collect: (monitor) => ({
        isAnotherAppointmentBeingDragged:
          monitor.getItemType() === CalendarDndItemTypes.APPOINTMENT,
      }),
    }),
    [],
  );

  const isFocused = useMemo(
    () => focusedBlockedInterval?.timeslot_id === blockedInterval.timeslot_id,
    [blockedInterval.timeslot_id, focusedBlockedInterval],
  );

  const isInThePast = useMemo(
    () => new Date(blockedInterval.end).valueOf() < Date.now(),
    [blockedInterval.end],
  );

  const editCurrentBlockedInterval = useCallback(
    () => dispatch(showBlockIntervalDetails(blockedInterval)),
    [blockedInterval, dispatch],
  );

  const onClick = useCallback(() => {
    if (
      currentSidesheet === SidesheetType.AddToCalendar &&
      requestCancelAddNewSession
    ) {
      requestCancelAddNewSession(editCurrentBlockedInterval);
    } else {
      editCurrentBlockedInterval();
    }
  }, [
    currentSidesheet,
    editCurrentBlockedInterval,
    requestCancelAddNewSession,
  ]);

  const timeslotName = useMemo(
    () => blockedInterval.busy_note || `(${messages.noTitle})`,
    [blockedInterval.busy_note],
  );

  return (
    <TimeslotButton
      className={getClasses(
        styles["blocked-interval-timeslot"],
        isInThePast && styles["blocked-interval-timeslot--past"],
      )}
      onClick={onClick}
      startDate={blockedInterval.calendarStart || blockedInterval.start}
      endDate={blockedInterval.calendarEnd || blockedInterval.end}
      timeslotName={timeslotName}
      isFocused={isFocused}
      date={date}
      style={{
        ...(isAnotherAppointmentBeingDragged ? { zIndex: 1 } : {}),
        ...(daySpan > 1
          ? { width: `calc(${daySpan * 100}% + ${(daySpan - 2) * 8}px)` }
          : {}),
      }}
      calendarColumnPosition={calendarColumnPosition}
    />
  );
};

export default BlockedIntervalTimeslot;
